import { SubscriptionProductId } from '@fruitful/types';
import { useEffect, useState } from 'react';
import { Linking } from 'react-native';
import {
    ArrowRightIcon,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChevronDownIcon,
    ChevronUpIcon,
    HistoryIcon,
    Icon,
    isWeb,
    NavigationTile,
    SegmentedControl,
    Sheet,
    SparklesIcon,
    UserPlusIcon,
    XStack,
    YStack,
} from 'smoothie';

import { getWebUrl } from '../../../lib/Web';

interface CreateNewMembershipSheetProps {
    isOpen: boolean;
    onClose: () => void;
}

type SkipTrialProductId =
    | SubscriptionProductId.STANDARD
    | SubscriptionProductId.PREMIUM;

const SKIP_TRIAL_OPTIONS: Array<{
    value: SkipTrialProductId;
    label: string;
}> = [
    { value: SubscriptionProductId.STANDARD, label: 'Fruitful (Standard)' },
    { value: SubscriptionProductId.PREMIUM, label: 'Fruitful Premium' },
];

const getNewMemberPath = (subscriptionProductId?: SkipTrialProductId) => {
    if (
        subscriptionProductId === SubscriptionProductId.STANDARD ||
        subscriptionProductId === SubscriptionProductId.PREMIUM
    ) {
        return `/dev/conv-mm-account-form?subscriptionProductId=${subscriptionProductId}`;
    }

    return '/dev/conv-mm-account-form';
};

const CreateNewMembershipSheet = ({
    isOpen,
    onClose,
}: CreateNewMembershipSheetProps) => {
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [skipTrialProductId, setSkipTrialProductId] =
        useState<SkipTrialProductId>(SubscriptionProductId.STANDARD);

    useEffect(() => {
        if (isOpen) {
            return;
        }
        setIsAdvancedOpen(false);
        setSkipTrialProductId(SubscriptionProductId.STANDARD);
    }, [isOpen]);

    const goTo = (path: string) => {
        onClose();
        if (isWeb) {
            window.location.href = path;
        } else {
            void Linking.openURL(`${getWebUrl()}${path}`);
        }
    };

    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Create a new membership"
            showCloseButton
            forceKeyboardCompatibleBottomSheet
        >
            <YStack gap={12} $md={{ minWidth: 400 }}>
                <NavigationTile
                    title="New signup, standard"
                    description="Starting at Conversational Money Map (CMM)"
                    iconLeft={SparklesIcon}
                    iconRight={ArrowRightIcon}
                    onPress={() => {
                        goTo('/sign-up/get-started');
                    }}
                />
                <NavigationTile
                    title="New signup, skipping CMM"
                    description="Go straight to new member creation"
                    iconLeft={UserPlusIcon}
                    iconRight={ArrowRightIcon}
                    onPress={() => {
                        goTo(getNewMemberPath());
                    }}
                />
                <NavigationTile
                    title="New signup, canned CMM"
                    description="Pick a scenario and skip the conversation"
                    iconLeft={SparklesIcon}
                    iconRight={ArrowRightIcon}
                    onPress={() => {
                        goTo('/dev/cmm-scenario');
                    }}
                />

                <XStack
                    alignItems="center"
                    justifyContent="space-between"
                    paddingVertical={8}
                    cursor="pointer"
                    onPress={() => {
                        setIsAdvancedOpen((wasOpen) => !wasOpen);
                    }}
                >
                    <BodyMediumRegular>Advanced</BodyMediumRegular>
                    <Icon
                        icon={isAdvancedOpen ? ChevronUpIcon : ChevronDownIcon}
                        size={16}
                        color="$gray1200"
                    />
                </XStack>

                {isAdvancedOpen && (
                    <YStack gap={16}>
                        <YStack
                            gap={12}
                            padding={16}
                            backgroundColor="$gray200"
                            borderRadius={20}
                        >
                            <YStack gap={4}>
                                <BodyMediumRegular>
                                    New user, bypassing the Premium Trial stage
                                </BodyMediumRegular>
                            </YStack>
                            <SegmentedControl<SkipTrialProductId>
                                selected={skipTrialProductId}
                                defaultValue={SubscriptionProductId.STANDARD}
                                onValueChange={(productId) => {
                                    if (
                                        productId ===
                                            SubscriptionProductId.STANDARD ||
                                        productId ===
                                            SubscriptionProductId.PREMIUM
                                    ) {
                                        setSkipTrialProductId(productId);
                                    }
                                }}
                                size="small"
                                options={SKIP_TRIAL_OPTIONS}
                            />
                            <Button
                                text="Create with selected product"
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                width="full"
                                onPress={() => {
                                    goTo(getNewMemberPath(skipTrialProductId));
                                }}
                            />
                        </YStack>
                        <NavigationTile
                            title="Legacy signup"
                            description="Original Start Membership path"
                            iconLeft={HistoryIcon}
                            iconRight={ArrowRightIcon}
                            onPress={() => {
                                goTo('/start-membership');
                            }}
                        />
                    </YStack>
                )}
            </YStack>
        </Sheet>
    );
};

export default CreateNewMembershipSheet;
